import React, { useState, useEffect, useRef } from "react";
import { useTranslations } from "use-intl";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MatchSuggestion, ManualMatchSearchResult, OwnAccountCandidate, Transaction } from "@/types/payments";
import { toast } from "sonner";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { Loader2, RefreshCw, AlertTriangle } from "lucide-react";
import { fetchWithPrecomputedFallback } from "@/utils/matchSuggestionFallback";
import { handleError } from "@/utils/handleError";
import { isSettlementAmountMismatch } from "@/utils/settlementAmount";
import { useFormatters } from "@financialharmony/ui";
import { useFormatOptionalDate } from "@/i18n/useFormatOptionalDate";

// product-feedback-30-aug.F4 (issue #904) — debounce interval for the manual-match free-text
// search box (no minimum character count, but not fired on every keystroke).
const SEARCH_DEBOUNCE_MS = 300;

/** Converts a search result into the same MatchSuggestion shape the suggestions table uses, so
 * selecting/confirming it goes through the exact same flow as a suggested candidate (F4 spec) —
 * `confidence: 0` is never displayed for a search-derived selection (see the render branch
 * below, which only shows a confidence column for the suggestions table). */
function searchResultToSuggestion(result: ManualMatchSearchResult): MatchSuggestion {
  if (result.documentType === "payable") {
    return {
      documentType: "payable",
      payableId: result.id,
      vendor: result.vendorOrCustomer,
      amount: result.amount,
      currency: result.currency,
      dueDate: result.dueDate,
      confidence: 0,
    };
  }
  return {
    documentType: "receivable",
    receivableId: result.id,
    customer: result.vendorOrCustomer,
    amount: result.amount,
    currency: result.currency,
    dueDate: result.dueDate,
    confidence: 0,
    remittanceInformation: result.remittanceInformation ?? "",
  };
}

interface MatchSuggestionsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  transaction: Transaction | null;
  // Issue #850 — Promise<boolean> (previously sync/void, closing the modal unconditionally
  // right after firing this off, regardless of whether the underlying write succeeded).
  handleConfirmMatch: (transactionId: string, receivableId: string) => Promise<boolean>;
  /**
   * Issue #497 — the payable-side counterpart of `handleConfirmMatch`, kept as a SEPARATE prop
   * (ratified Q4) rather than collapsing both write paths into one signature: the two backend
   * writes are genuinely non-interchangeable (different parameter order, different owning hooks).
   * Both are now async and only close the modal on confirmed success (issue #850). Mirrors
   * `PayableMatchSuggestionsModal.tsx`'s existing `onConfirmMatch?: (payableId, transactionId) =>
   * Promise<boolean>` contract. Required — both real callers (Transactions.tsx, Bank.tsx) have
   * wired this up since issue #497 Task 4; a modal that can render a payable suggestion (any
   * debit transaction) without a working confirm handler would be a silent dead end.
   */
  onConfirmPayableMatch: (payableId: string, transactionId: string) => Promise<boolean>;
  /**
   * FIN-976 F6: pairs the transaction with a tax account row (an own-account transfer). Resolves
   * true only once the match is written, like the two confirm handlers above.
   */
  onConfirmOwnAccountMatch: (transaction: Transaction, candidate: OwnAccountCandidate) => Promise<boolean>;
  formatCurrency: (amount: number, currency: string) => string;
}

/** FIN-976 F6: the tax account rows offered for this transaction; none without its row id. */
function fetchOwnAccountCandidates(transaction: Transaction): Promise<OwnAccountCandidate[]> {
  if (!transaction.bankTransactionId) return Promise.resolve([]);
  return ServiceRegistry.getTransactionService().listOwnAccountCandidates(transaction.bankTransactionId);
}

/** Returns the identifier that uniquely keys a suggestion row, regardless of documentType. */
function suggestionKey(suggestion: MatchSuggestion): string {
  return suggestion.documentType === "payable" ? suggestion.payableId : suggestion.receivableId;
}

/**
 * Issue #457: try the precomputed auto_match_decisions suggestion first (already computed by the
 * async match_jobs pipeline); fall back to the live JIT scoring query only when that's empty —
 * e.g. nothing has been scored yet for this transaction. `forceRefresh` (the explicit "Search
 * again" action) always calls the JIT path directly, regardless of what's precomputed, so a
 * transaction too new for the ~1-minute match-job cron to have scored yet can still be found.
 * This is only true for a CREDIT transaction — see the debit gate below.
 *
 * Whole-branch review follow-up (issue #497, Finding 2): `fetchWithPrecomputedFallback`'s
 * "precomputed threw -> silently swallow and fall through to JIT" policy is only safe when JIT
 * is a genuinely meaningful retry. JIT (`transactionService.getMatchSuggestions`) is
 * receivable-only (see `MatchingService.getPossibleMatches`'s own docstring on the backend) —
 * for a debit transaction it structurally cannot find anything, so it always resolves to `[]`
 * regardless of whether the precomputed call failed because of a real bug or because there's
 * genuinely nothing there. Before this fix, a real bug in the new #497 payable-resolution code
 * would be silently masked: the modal would render "No matching suggestions found" instead of
 * the `loadFailed` error state, even though something had actually gone wrong.
 *
 * Whole-branch review follow-up (issue #497, Finding 3 — caught in a second independent
 * architect-review pass, after Finding 2 above was already fixed): the Finding 2 fix only gated
 * the *initial load* (`forceRefresh === false`) on `transaction.type === "debit"`. "Search
 * again" (`forceRefresh === true`) still fell straight into `fetchWithPrecomputedFallback`,
 * which for `forceRefresh === true` calls `getJit()` UNCONDITIONALLY — with no direction check
 * at all. `getJit()`'s receivable scoring (`MatchingService.getPossibleMatches`) is not gated by
 * transaction direction on the backend either (only the *write* path, `scorePayableSuggestions`,
 * is direction-gated) — so a debit transaction's "Search again" could genuinely surface a
 * receivable suggestion (e.g. currency match +20, near-amount +5, date-proximity +10 clears the
 * `>= 30` floor with no real relationship to the transaction), and confirming it would persist a
 * receivable match against an outgoing payment via `handleConfirmMatch` — the exact
 * mixed-direction outcome the whole precomputed-path gating in `matchingService.ts` exists to
 * prevent. This was also reachable as the RETRY action from the `loadFailed` error state itself
 * (the "Use 'Search again' below to retry" copy), making it the most likely path a user actually
 * takes after a transient failure.
 *
 * Fix: the debit short-circuit below is now unconditional on `transaction.type`, not on
 * `!forceRefresh` — a debit transaction's "Search again" re-fetches the precomputed path (the
 * only source of payable suggestions), never `getJit()`. This does cost debit transactions the
 * "just-synced, not yet cron-scored" JIT retry credit transactions get — acceptable, since
 * JIT has no payable-scoring equivalent to retry into anyway (see Finding 2's rationale). Credit
 * transactions are unaffected: `getJit()`'s receivable scoring remains a meaningful retry there.
 */
function fetchSuggestions(
  transaction: Transaction,
  forceRefresh: boolean,
): Promise<MatchSuggestion[]> {
  const transactionService = ServiceRegistry.getTransactionService();
  const getPrecomputed = () => transactionService.getPrecomputedMatchSuggestions(transaction.id);
  const getJit = () => transactionService.getMatchSuggestions(transaction.id, transaction.accountId);

  if (transaction.type === "debit") {
    return getPrecomputed();
  }

  return fetchWithPrecomputedFallback(getPrecomputed, getJit, forceRefresh);
}

const MatchSuggestionsModal = ({
  open,
  setOpen,
  transaction,
  handleConfirmMatch,
  onConfirmPayableMatch,
  onConfirmOwnAccountMatch,
  formatCurrency
}: MatchSuggestionsModalProps) => {
  const t = useTranslations("consultantBank.matchModal");
  // The latest translator, so the loaders the effects call word their messages without being rebuilt.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);
  const { formatDate } = useFormatters();
  const formatOptionalDate = useFormatOptionalDate();
  const [selectedSuggestion, setSelectedSuggestion] = useState<MatchSuggestion | null>(null);
  // FIN-976 F6: the "Tax account" section. Its own load state, so a failure there never empties
  // or blocks the suggestions above, and a selection there excludes one in the other lists.
  const [ownAccountCandidates, setOwnAccountCandidates] = useState<OwnAccountCandidate[]>([]);
  const [ownAccountLoading, setOwnAccountLoading] = useState(false);
  const [ownAccountLoadFailed, setOwnAccountLoadFailed] = useState(false);
  const [selectedOwnAccount, setSelectedOwnAccount] = useState<OwnAccountCandidate | null>(null);
  const [suggestions, setSuggestions] = useState<MatchSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // Distinct from `suggestions.length === 0` — a genuinely empty result still renders the
  // existing empty-state row, not this error state (issue #497 Q5 fix; see
  // `.claude/rules/client-error-handling.md`'s Tier 2 pattern, applied here even though this
  // component lives in `src/`, not `apps/client/`).
  const [loadFailed, setLoadFailed] = useState(false);

  // product-feedback-30-aug.F4 (issue #904) — Manually Match free-text search fallback.
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<ManualMatchSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  // Distinct from `searchResults.length === 0` — a genuinely empty result (EC6) must render
  // differently from "nothing searched yet" and from "currently searching".
  const [searchHasRun, setSearchHasRun] = useState(false);
  // EC9: only a search-derived selection can go stale (matched elsewhere before Confirm) in a
  // way this modal can detect (onConfirmPayableMatch's real success/failure signal) — a
  // suggestions-table selection keeps its existing behavior unchanged.
  const [selectedFromSearch, setSelectedFromSearch] = useState(false);

  const runSearch = async (direction: "debit" | "credit", term: string) => {
    setSearchLoading(true);
    try {
      const service = ServiceRegistry.getTransactionService();
      const results = await service.searchUnmatchedDocuments(direction, term);
      setSearchResults(results);
    } catch (e) {
      await handleError(e, tRef.current("errors.searchFailed"), "MatchSuggestionsModal.runSearch");
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
      setSearchHasRun(true);
    }
  };

  useEffect(() => {
    if (!transaction) return;
    const trimmed = searchQuery.trim();
    if (!trimmed) {
      setSearchResults([]);
      setSearchHasRun(false);
      return;
    }
    const direction = transaction.type === "debit" ? "debit" : "credit";
    const timeoutId = setTimeout(() => runSearch(direction, trimmed), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, transaction]);

  const loadOwnAccountCandidates = async (forTransaction: Transaction, context: string) => {
    setOwnAccountLoading(true);
    setOwnAccountLoadFailed(false);
    try {
      setOwnAccountCandidates(await fetchOwnAccountCandidates(forTransaction));
    } catch (e) {
      await handleError(e, tRef.current("errors.loadFailed"), context);
      setOwnAccountCandidates([]);
      setOwnAccountLoadFailed(true);
    } finally {
      setOwnAccountLoading(false);
    }
  };

  useEffect(() => {
    const loadSuggestions = async () => {
      // Reset the search box too — a new transaction/open cycle starts with no manual search
      // in progress, never a stale query or results carried over from the previous transaction.
      setSearchQuery("");
      setSearchResults([]);
      setSearchHasRun(false);
      setSelectedFromSearch(false);
      // FIN-976: never preselected, even when a tax row has the transaction's exact amount (EC3).
      setSelectedOwnAccount(null);
      if (open && transaction) {
        // Loaded alongside the suggestions, not awaited: each section has its own error state.
        loadOwnAccountCandidates(transaction, "MatchSuggestionsModal.loadOwnAccountCandidates");
        setLoading(true);
        setLoadFailed(false);
        setSelectedSuggestion(null);
        try {
          const result = await fetchSuggestions(transaction, false);
          setSuggestions(result);
        } catch (e) {
          await handleError(e, tRef.current("errors.loadFailed"), "MatchSuggestionsModal.loadSuggestions");
          setSuggestions([]);
          setLoadFailed(true);
        } finally {
          setLoading(false);
        }
      } else {
        setSuggestions([]);
        setLoadFailed(false);
        setOwnAccountCandidates([]);
        setOwnAccountLoadFailed(false);
      }
    };
    loadSuggestions();
  }, [open, transaction]);

  const handleSearchAgain = async () => {
    if (!transaction) return;
    loadOwnAccountCandidates(transaction, "MatchSuggestionsModal.handleSearchAgain.ownAccount");
    setLoading(true);
    setLoadFailed(false);
    try {
      const result = await fetchSuggestions(transaction, true);
      setSuggestions(result);
    } catch (e) {
      await handleError(e, t("errors.loadFailed"), "MatchSuggestionsModal.handleSearchAgain");
      setSuggestions([]);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (suggestion: MatchSuggestion) => {
    setSelectedSuggestion(suggestion);
    setSelectedFromSearch(false);
    setSelectedOwnAccount(null);
  };

  const handleSelectSearchResult = (result: ManualMatchSearchResult) => {
    setSelectedSuggestion(searchResultToSuggestion(result));
    setSelectedFromSearch(true);
    setSelectedOwnAccount(null);
  };

  const handleSelectOwnAccount = (candidate: OwnAccountCandidate) => {
    setSelectedOwnAccount(candidate);
    setSelectedSuggestion(null);
    setSelectedFromSearch(false);
  };

  const selectedDocument = selectedOwnAccount ?? selectedSuggestion;
  const amountMismatch = !!transaction && !!selectedDocument &&
    isSettlementAmountMismatch(selectedDocument, transaction);

  const handleConfirm = async () => {
    if (transaction && selectedOwnAccount) {
      setConfirming(true);
      try {
        if (await onConfirmOwnAccountMatch(transaction, selectedOwnAccount)) {
          setOpen(false);
          setSelectedOwnAccount(null);
        }
      } finally {
        setConfirming(false);
      }
      return;
    }

    if (!transaction || !selectedSuggestion) {
      toast.error(t("toasts.selectMatch"));
      return;
    }

    if (selectedSuggestion.documentType === "payable") {
      setConfirming(true);
      try {
        const success = await onConfirmPayableMatch(selectedSuggestion.payableId, transaction.id);
        if (success) {
          setOpen(false);
          setSelectedSuggestion(null);
        } else if (selectedFromSearch) {
          // EC9 (product-feedback-30-aug.F4, issue #904): the selected search result was matched
          // by someone/something else between the search and Confirm — a clear message, not a
          // silent no-op, and refresh the results rather than leaving the stale row selectable.
          toast.error(t("toasts.noLongerAvailable"));
          setSelectedSuggestion(null);
          setSelectedFromSearch(false);
          const trimmed = searchQuery.trim();
          if (trimmed) {
            await runSearch(transaction.type === "debit" ? "debit" : "credit", trimmed);
          }
        }
      } finally {
        setConfirming(false);
      }
      return;
    }

    // Issue #850 — mirrors the payable branch above: await the write and only close on
    // confirmed success, instead of closing unconditionally right after firing it off.
    setConfirming(true);
    try {
      const success = await handleConfirmMatch(transaction.id, selectedSuggestion.receivableId);
      if (success) {
        setOpen(false);
        setSelectedSuggestion(null);
      }
    } finally {
      setConfirming(false);
    }
  };

  // FIN-976 F6: loading, failed and loaded are separate states; empty is the bare header row,
  // because both existing empty-state sentences already belong to the lists around this section.
  const renderOwnAccountCandidates = () => {
    if (ownAccountLoading) {
      return (
        <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          {t("loading")}
        </div>
      );
    }
    if (ownAccountLoadFailed) {
      return (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t("loadFailedTitle")}</AlertTitle>
          <AlertDescription>
            {t("loadFailedBody")}
          </AlertDescription>
        </Alert>
      );
    }
    return (
      <div className="max-h-[240px] overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("columns.reference")}</TableHead>
              <TableHead>{t("columns.amount")}</TableHead>
              <TableHead>{t("columns.date")}</TableHead>
              <TableHead>{t("columns.action")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ownAccountCandidates.map((candidate) => {
              const isSelected = selectedOwnAccount?.legId === candidate.legId;
              return (
                <TableRow key={candidate.legId} className={isSelected ? "bg-muted" : ""}>
                  <TableCell>{candidate.label}</TableCell>
                  <TableCell>{formatCurrency(candidate.amount, candidate.currency)}</TableCell>
                  <TableCell>{formatDate(candidate.date, "numeric")}</TableCell>
                  <TableCell>
                    <Button variant="outline" size="sm" onClick={() => handleSelectOwnAccount(candidate)}>
                      {isSelected ? t("selected") : t("select")}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedSuggestion(null);
    setSelectedFromSearch(false);
    setSelectedOwnAccount(null);
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return "text-green-600";
    if (confidence >= 70) return "text-emerald-600";
    if (confidence >= 50) return "text-amber-600";
    return "text-red-600";
  };

  // Suggestions are homogeneous per fetch: the backend symmetrically gates payable resolution to
  // debit transactions only, and receivable resolution to credit transactions only (whole-branch
  // review follow-up, issue #497 — see MatchingService.getPrecomputedMatchSuggestionsForTransaction),
  // so the first row's documentType safely decides the table's column layout.
  const isPayableSuggestions = suggestions.length > 0 && suggestions[0].documentType === "payable";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("title")}</DialogTitle>
          <DialogDescription>
            {transaction ? (
              <div className="text-sm mt-2">
                <p>{t("transaction", { description: transaction.description ?? "" })}</p>
                <p>{t("amount", { amount: formatCurrency(transaction.amount, transaction.currency) })}</p>
                <p>{t("date", { date: formatDate(transaction.date, "numeric") })}</p>
                {transaction.counterpartyName && (
                  // Issue #523 — this is the surface where an accountant picks a match candidate;
                  // surface who the counterparty is here too, not just in TransactionDetailDialog.
                  <p>
                    {transaction.counterpartyIban
                      ? t("counterpartyIban", { name: transaction.counterpartyName, iban: transaction.counterpartyIban })
                      : t("counterparty", { name: transaction.counterpartyName })}
                  </p>
                )}
              </div>
            ) : (
              <p>{t("selectReceivable")}</p>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="ml-2">{t("loading")}</span>
            </div>
          ) : loadFailed ? (
            // Distinct render branch from the empty-state row below — a fetch failure must never
            // be conflated with "genuinely zero suggestions" (issue #497 Q5 fix).
            <div className="p-4">
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>{t("loadFailedTitle")}</AlertTitle>
                <AlertDescription>
                  {t("loadFailedBody")}
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {!isPayableSuggestions && <TableHead>{t("columns.remittance")}</TableHead>}
                  <TableHead>{isPayableSuggestions ? t("columns.vendor") : t("columns.customer")}</TableHead>
                  <TableHead>{t("columns.amount")}</TableHead>
                  <TableHead>{t("columns.dueDate")}</TableHead>
                  <TableHead>{t("columns.confidence")}</TableHead>
                  <TableHead>{t("columns.action")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suggestions.length > 0 ? (
                  suggestions.map((suggestion) => {
                    const key = suggestionKey(suggestion);
                    const isSelected = selectedSuggestion !== null && suggestionKey(selectedSuggestion) === key;
                    return (
                      <TableRow key={key} className={isSelected ? "bg-muted" : ""}>
                        {suggestion.documentType === "receivable" && (
                          <TableCell>{suggestion.remittanceInformation}</TableCell>
                        )}
                        <TableCell>
                          {suggestion.documentType === "payable" ? suggestion.vendor : suggestion.customer}
                        </TableCell>
                        <TableCell>{formatCurrency(suggestion.amount, suggestion.currency)}</TableCell>
                        <TableCell>{formatOptionalDate(suggestion.dueDate)}</TableCell>
                        <TableCell>
                          <span className={getConfidenceColor(suggestion.confidence)}>
                            {suggestion.confidence.toFixed(1)}%
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSelect(suggestion)}
                          >
                            {isSelected ? t("selected") : t("select")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={isPayableSuggestions ? 5 : 6} className="h-24 text-center">
                      {t("noSuggestions")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {/* FIN-976 F6 — always shown: the company's own tax account rows moving the other way. */}
        <div className="space-y-2 border-t pt-4" role="group" aria-labelledby="own-account-candidates-heading">
          <h3 id="own-account-candidates-heading" className="text-sm font-medium leading-none">{t("taxAccount")}</h3>
          {renderOwnAccountCandidates()}
        </div>

        <div className="text-center">
          <Button variant="link" size="sm" onClick={handleSearchAgain} disabled={loading}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            {t("searchAgain")}
          </Button>
        </div>

        {/* product-feedback-30-aug.F4 (issue #904) — free-text search fallback, always
            available regardless of suggestion state. */}
        <div className="space-y-2 border-t pt-4">
          <Label htmlFor="manual-match-search">{t("manualSearch")}</Label>
          <Input
            id="manual-match-search"
            placeholder={t("searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchLoading ? (
            <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              {t("searching")}
            </div>
          ) : searchHasRun ? (
            searchResults.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">{t("noMatches")}</p>
            ) : (
              <div data-testid="manual-match-search-results" className="max-h-[240px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{searchResults[0].documentType === "payable" ? t("columns.vendor") : t("columns.customer")}</TableHead>
                      <TableHead>{t("columns.amount")}</TableHead>
                      <TableHead>{t("columns.dueDate")}</TableHead>
                      <TableHead>{t("columns.reference")}</TableHead>
                      <TableHead>{t("columns.uploaded")}</TableHead>
                      <TableHead>{t("columns.action")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {searchResults.map((result) => {
                      const isSelected =
                        selectedFromSearch && selectedSuggestion !== null && suggestionKey(selectedSuggestion) === result.id;
                      return (
                        <TableRow key={result.id} className={isSelected ? "bg-muted" : ""}>
                          <TableCell>{result.vendorOrCustomer}</TableCell>
                          <TableCell>{formatCurrency(result.amount, result.currency)}</TableCell>
                          <TableCell>{formatOptionalDate(result.dueDate)}</TableCell>
                          <TableCell>{result.remittanceInformation || "—"}</TableCell>
                          <TableCell>{result.createdAt ? formatDate(result.createdAt, "numeric") : "—"}</TableCell>
                          <TableCell>
                            <Button variant="outline" size="sm" onClick={() => handleSelectSearchResult(result)}>
                              {isSelected ? t("selected") : t("select")}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )
          ) : null}
        </div>

        {amountMismatch && transaction && selectedDocument && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t("mismatchTitle")}</AlertTitle>
            <AlertDescription>
              {t("mismatchBody", {
                transactionAmount: formatCurrency(Math.abs(transaction.amount), transaction.currency),
                documentAmount: formatCurrency(Math.abs(selectedDocument.amount), selectedDocument.currency),
              })}
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedDocument || confirming || amountMismatch}
          >
            {confirming ? t("confirming") : t("confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MatchSuggestionsModal;
